// Per-diatom studio-mixer state: mute / solo / gain / excitation override, plus the
// mute-solo audibility rules. Pure state — no audio, no DOM — so it is unit-testable and
// keeps App.ts focused on wiring.

import type { Excitation } from './studio';
import type { SceneDiatom } from './scenes';

interface DiatomState {
  gain: number;
  excitation: Excitation;
}

export class DiatomMixer {
  private readonly muted = new Set<string>();
  private readonly soloed = new Set<string>();
  private readonly perDiatom = new Map<string, DiatomState>();

  /** Whether a diatom is currently allowed to sound (any solo overrides mute). */
  audible(id: string): boolean {
    if (this.soloed.size > 0 && !this.soloed.has(id)) return false;
    return !this.muted.has(id);
  }

  /** True when at least one diatom is soloed. */
  get soloing(): boolean {
    return this.soloed.size > 0;
  }

  isSoloed(id: string): boolean {
    return this.soloed.has(id);
  }

  soloedIds(): string[] {
    return [...this.soloed];
  }

  /** Per-diatom gain + excitation override, if one has been set. */
  entry(id: string): Readonly<DiatomState> | undefined {
    return this.perDiatom.get(id);
  }

  setMuted(id: string, muted: boolean): void {
    if (muted) this.muted.add(id);
    else this.muted.delete(id);
  }

  setSolo(id: string, solo: boolean): void {
    if (solo) {
      this.soloed.add(id);
      this.muted.delete(id); // soloing a diatom clears its mute
    } else {
      this.soloed.delete(id);
    }
  }

  setGain(id: string, gain: number): void {
    const pd = this.perDiatom.get(id) ?? { gain: 1, excitation: 'auto' as const };
    pd.gain = gain;
    this.perDiatom.set(id, pd);
  }

  setExcitation(id: string, excitation: Excitation): void {
    const pd = this.perDiatom.get(id) ?? { gain: 1, excitation: 'auto' as const };
    pd.excitation = excitation;
    this.perDiatom.set(id, pd);
  }

  /** Capture a diatom's full mixer state for scene persistence. */
  capture(id: string): SceneDiatom {
    const pd = this.perDiatom.get(id);
    return {
      gain: pd?.gain ?? 1,
      excitation: pd?.excitation ?? 'auto',
      muted: this.muted.has(id),
      soloed: this.soloed.has(id),
    };
  }

  /** Replace all state from a saved scene's per-diatom map. */
  load(diatoms: Record<string, SceneDiatom>): void {
    this.muted.clear();
    this.soloed.clear();
    this.perDiatom.clear();
    for (const [id, s] of Object.entries(diatoms)) {
      this.perDiatom.set(id, { gain: s.gain, excitation: s.excitation });
      if (s.muted) this.muted.add(id);
      if (s.soloed) this.soloed.add(id);
    }
  }
}
